import React, { useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

const normalizeBase = (u) => (u || "").replace(/\/+$/, "");

const chipStyle = {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    color: "#374151",
};

const buttonBaseStyle = {
    padding: "12px 16px",
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 14,
};

function SummaryCard({ payload }) {
    const summary = payload?.summary || {};
    const report = payload?.report || {};
    const warnings = summary.warnings || report.warnings || [];
    const items = summary.items || report.items || [];

    return (
        <div
            style={{
                border: "1px solid #e5e7eb",
                borderRadius: 18,
                padding: 18,
                background: "#ffffff",
                boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
            }}
        >
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>
                    Conversion Summary
                </div>

                <div style={chipStyle}>
                    Items Found: {summary.itemsFound ?? report.itemsFound ?? 0}
                </div>

                <div style={chipStyle}>
                    Items Converted: {summary.itemsConverted ?? report.itemsConverted ?? 0}
                </div>

                <div
                    style={{
                        ...chipStyle,
                        background: warnings.length ? "#fffbeb" : "#ecfdf5",
                        border: warnings.length ? "1px solid #fde68a" : "1px solid #a7f3d0",
                        color: warnings.length ? "#92400e" : "#065f46",
                    }}
                >
                    {warnings.length ? `${warnings.length} Warning${warnings.length === 1 ? "" : "s"}` : "No Warnings"}
                </div>
            </div>

            {payload?.conversionId && (
                <div style={{ marginTop: 12, fontSize: 13, color: "#6b7280" }}>
                    Conversion ID: <strong>{payload.conversionId}</strong>
                </div>
            )}

            {Array.isArray(warnings) && warnings.length > 0 && (
                <div style={{ marginTop: 18 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#92400e", marginBottom: 8 }}>
                        Warnings
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 20, color: "#374151", lineHeight: 1.6 }}>
                        {warnings.map((w, i) => (
                            <li key={i}>{String(w)}</li>
                        ))}
                    </ul>
                </div>
            )}

            {Array.isArray(items) && items.length > 0 && (
                <div style={{ marginTop: 18 }}>
                    <div style={{ fontWeight: 800, fontSize: 14, color: "#111827", marginBottom: 8 }}>
                        Converted Items
                    </div>

                    <div
                        style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 14,
                            overflow: "hidden",
                        }}
                    >
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead>
                            <tr style={{ background: "#f9fafb" }}>
                                <th style={thStyle}>Title</th>
                                <th style={thStyle}>Source Type</th>
                                <th style={thStyle}>Output</th>
                                <th style={thStyle}>Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {items.map((item, i) => (
                                <tr key={i}>
                                    <td style={tdStyle}>{item.title || item.identifier || `Item ${i + 1}`}</td>
                                    <td style={tdStyle}>{item.sourceType || item.type || ""}</td>
                                    <td style={tdStyle}>{item.outputType || item.interaction || ""}</td>
                                    <td style={tdStyle}>
                                            <span
                                                style={{
                                                    ...chipStyle,
                                                    padding: "3px 8px",
                                                    fontSize: 12,
                                                    background: item.status === "warning" ? "#fffbeb" : "#ecfdf5",
                                                    border:
                                                        item.status === "warning"
                                                            ? "1px solid #fde68a"
                                                            : "1px solid #a7f3d0",
                                                    color: item.status === "warning" ? "#92400e" : "#065f46",
                                                }}
                                            >
                                                {item.status || "converted"}
                                            </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function QtiConverterWidget({ backEndUrl, tenantId }) {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const token = query.get("token") || "";

    const [qtiZip, setQtiZip] = useState(null);
    const [busy, setBusy] = useState(false);
    const [statusText, setStatusText] = useState("");
    const [error, setError] = useState("");
    const [payload, setPayload] = useState(null);

    const canSubmit = !!(qtiZip && token && backEndUrl) && !busy;

    async function convertPackage() {
        setError("");
        setStatusText("");
        setPayload(null);

        if (!canSubmit) return;

        try {
            setBusy(true);

            const base = normalizeBase(backEndUrl);

            if (!String(qtiZip?.name || "").toLowerCase().endsWith(".zip")) {
                throw new Error("Please select a QTI 1.2 .zip package.");
            }

            setStatusText("Requesting secure upload URL...");

            const { data: presign } = await axios.post(
                `${base}/qti-converter/upload-url`,
                {
                    tenantId,
                    fileName: qtiZip.name,
                    fileSize: qtiZip.size,
                    contentType: qtiZip.type || "application/zip",
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                    },
                    timeout: 60000,
                }
            );

            const conversionId = presign?.conversionId;
            const upload = presign?.upload;

            if (!conversionId || !upload?.uploadUrl || !upload?.key) {
                throw new Error("Upload URL response was incomplete.");
            }

            setStatusText("Uploading QTI 1.2 package...");

            await axios.put(upload.uploadUrl, qtiZip, {
                headers: {
                    "Content-Type": upload.contentType || qtiZip.type || "application/zip",
                },
                maxBodyLength: Infinity,
                maxContentLength: Infinity,
                timeout: 30 * 60 * 1000,
            });

            setStatusText("Starting conversion...");

            const { data: started } = await axios.post(
                `${base}/qti-converter/convert`,
                {
                    tenantId,
                    conversionId,
                    sourceKey: upload.key,
                    sourceFileName: qtiZip.name,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                        ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                    },
                    timeout: 60000,
                }
            );

            const queuedConversionId = started?.conversionId || conversionId;

            setStatusText(`Queued. Waiting for worker...`);

            const maxPolls = 180;

            for (let i = 0; i < maxPolls; i++) {
                const { data: status } = await axios.get(
                    `${base}/qti-converter/status/${encodeURIComponent(queuedConversionId)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                        },
                        timeout: 60000,
                    }
                );

                const currentStatus = status?.status || "PROCESSING";
                setStatusText(`Status: ${currentStatus}...`);

                if (currentStatus === "COMPLETED") {
                    setPayload({
                        ...status,
                        conversionId: status?.conversionId || queuedConversionId,
                    });
                    setStatusText("Conversion completed.");
                    return;
                }

                if (currentStatus === "FAILED") {
                    throw new Error(status?.error || "Conversion failed in the background worker.");
                }

                await new Promise((r) => setTimeout(r, 3000));
            }

            throw new Error("Conversion is still processing. Refresh the status or run the conversion again.");
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Conversion failed.");
            setStatusText("");
        } finally {
            setBusy(false);
        }
    }

    async function downloadConvertedZip() {
        try {
            setError("");

            if (!payload?.conversionId) {
                throw new Error("No completed conversion is available to download.");
            }

            const base = normalizeBase(backEndUrl);

            const { data } = await axios.get(
                `${base}/qti-converter/download/${encodeURIComponent(payload.conversionId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        ...(tenantId ? { "x-tenant-id": tenantId } : {}),
                    },
                    timeout: 60000,
                }
            );

            if (!data?.downloadUrl) {
                throw new Error("Download URL response was incomplete.");
            }

            window.location.href = data.downloadUrl;
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Download failed.");
        }
    }

    function downloadReportJson() {
        if (!payload) return;

        const report = {
            conversionId: payload.conversionId,
            status: payload.status,
            sourceFileName: payload.sourceFileName,
            outputFileName: payload.outputFileName,
            summary: payload.summary || {},
            report: payload.report || {},
            completedAt: payload.completedAt,
            createdAt: payload.createdAt,
        };

        const blob = new Blob([JSON.stringify(report, null, 2)], {
            type: "application/json",
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");

        a.href = url;
        a.download = `${payload.conversionId || "qti-conversion"}-report.json`;
        a.click();

        URL.revokeObjectURL(url);
    }

    function resetForm() {
        setQtiZip(null);
        setPayload(null);
        setError("");
        setStatusText("");
    }

    return (
        <div
            style={{
                maxWidth: 1040,
                margin: "32px auto",
                display: "grid",
                gap: 16,
                padding: "0 16px 24px",
                fontFamily: "Inter, Arial, sans-serif",
                color: "#111827",
            }}
        >
            <div
                style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 20,
                    padding: 24,
                    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
                }}
            >
                <div style={{ marginBottom: 20 }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 16,
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                        }}
                    >
                        <div>
                            <h2 style={{ margin: "0 0 8px", fontSize: 24, lineHeight: 1.2 }}>
                                QTI 1.2 to QTI 2.1 Converter
                            </h2>

                            <div style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.5, maxWidth: 760 }}>
                                Upload a QTI 1.2 package from an LMS or assessment tool, convert it on the
                                backend, and download a QTI 2.1 package.
                            </div>
                        </div>

                        <div
                            style={{
                                ...chipStyle,
                                background: token ? "#ecfdf5" : "#fef2f2",
                                border: token ? "1px solid #a7f3d0" : "1px solid #fecaca",
                                color: token ? "#065f46" : "#991b1b",
                            }}
                        >
                            {token ? "LTI token detected" : "Missing LTI token"}
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 16,
                        padding: 16,
                        background: "#ffffff",
                    }}
                >
                    <label style={{ display: "block", fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
                        QTI 1.2 Package ZIP
                    </label>

                    <input
                        key={qtiZip ? qtiZip.name : "empty"}
                        type="file"
                        accept=".zip,application/zip,application/x-zip-compressed"
                        onChange={(e) => {
                            setError("");
                            setStatusText("");
                            setPayload(null);
                            setQtiZip(e.target.files?.[0] || null);
                        }}
                    />

                    <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                        Select the exported QTI 1.2 ZIP package. The converter will generate a new QTI 2.1
                        package with one assessment item XML file per question.
                    </div>

                    {qtiZip && (
                        <div
                            style={{
                                marginTop: 12,
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                                alignItems: "center",
                            }}
                        >
                            <span style={chipStyle}>{qtiZip.name}</span>
                            <span style={chipStyle}>{formatBytes(qtiZip.size)}</span>
                        </div>
                    )}
                </div>

                <div
                    style={{
                        marginTop: 18,
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    <button
                        onClick={convertPackage}
                        disabled={!canSubmit}
                        style={{
                            ...buttonBaseStyle,
                            border: "1px solid #2563eb",
                            background: canSubmit ? "#2563eb" : "#93c5fd",
                            color: "#fff",
                            cursor: canSubmit ? "pointer" : "not-allowed",
                            boxShadow: canSubmit ? "0 6px 16px rgba(37, 99, 235, 0.25)" : "none",
                        }}
                    >
                        {busy ? "Converting..." : "Convert Package"}
                    </button>

                    {payload?.status === "COMPLETED" && (
                        <button
                            onClick={downloadConvertedZip}
                            style={{
                                ...buttonBaseStyle,
                                border: "1px solid #16a34a",
                                background: "#16a34a",
                                color: "#fff",
                                cursor: "pointer",
                                boxShadow: "0 6px 16px rgba(22, 163, 74, 0.2)",
                            }}
                        >
                            Download QTI 2.1 ZIP
                        </button>
                    )}

                    {payload?.status === "COMPLETED" && (
                        <button
                            onClick={downloadReportJson}
                            style={{
                                ...buttonBaseStyle,
                                border: "1px solid #d1d5db",
                                background: "#fff",
                                color: "#111827",
                                cursor: "pointer",
                            }}
                        >
                            Download Report JSON
                        </button>
                    )}

                    {(qtiZip || payload || error || statusText) && !busy && (
                        <button
                            onClick={resetForm}
                            style={{
                                ...buttonBaseStyle,
                                border: "1px solid #d1d5db",
                                background: "#fff",
                                color: "#374151",
                                cursor: "pointer",
                            }}
                        >
                            Reset
                        </button>
                    )}
                </div>

                {!backEndUrl && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: 12,
                            border: "1px solid #fecaca",
                            background: "#fef2f2",
                            color: "#991b1b",
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        Missing backend URL. Pass <code>backEndUrl</code> into this component.
                    </div>
                )}

                {!token && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: 12,
                            border: "1px solid #fde68a",
                            background: "#fffbeb",
                            color: "#92400e",
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        No LTI launch token was found in the URL. The backend calls expect an
                        Authorization bearer token.
                    </div>
                )}

                {statusText && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: 12,
                            border: "1px solid #bfdbfe",
                            background: "#eff6ff",
                            color: "#1e3a8a",
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        {statusText}
                    </div>
                )}

                {error && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: 12,
                            border: "1px solid #fecaca",
                            background: "#fef2f2",
                            color: "#991b1b",
                            borderRadius: 12,
                            fontSize: 13,
                            fontWeight: 500,
                        }}
                    >
                        {error}
                    </div>
                )}
            </div>

            {payload?.status === "COMPLETED" && <SummaryCard payload={payload} />}

            <div
                style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 18,
                    padding: 18,
                    background: "#ffffff",
                    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
                }}
            >
                <h3 style={{ marginTop: 0, marginBottom: 10 }}>Supported first-pass conversions</h3>

                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                        <tr style={{ background: "#f9fafb" }}>
                            <th style={thStyle}>QTI 1.2 / LMS Type</th>
                            <th style={thStyle}>QTI 2.1 Output</th>
                            <th style={thStyle}>Notes</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td style={tdStyle}>multiple_choice_question</td>
                            <td style={tdStyle}>choiceInteraction</td>
                            <td style={tdStyle}>Single correct response.</td>
                        </tr>
                        <tr>
                            <td style={tdStyle}>multiple_answers_question</td>
                            <td style={tdStyle}>choiceInteraction, multiple cardinality</td>
                            <td style={tdStyle}>Converted as all-or-nothing unless custom processing is added.</td>
                        </tr>
                        <tr>
                            <td style={tdStyle}>true_false_question</td>
                            <td style={tdStyle}>choiceInteraction</td>
                            <td style={tdStyle}>True/false choices are preserved.</td>
                        </tr>
                        <tr>
                            <td style={tdStyle}>fill_in_the_blank / short answer</td>
                            <td style={tdStyle}>textEntryInteraction</td>
                            <td style={tdStyle}>Basic correct text response support.</td>
                        </tr>
                        <tr>
                            <td style={tdStyle}>essay_question</td>
                            <td style={tdStyle}>extendedTextInteraction</td>
                            <td style={tdStyle}>Manual scoring expected.</td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const thStyle = {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 800,
    color: "#374151",
};

const tdStyle = {
    padding: "10px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#374151",
    verticalAlign: "top",
};

function formatBytes(bytes) {
    const n = Number(bytes || 0);

    if (!Number.isFinite(n) || n <= 0) {
        return "0 bytes";
    }

    const units = ["bytes", "KB", "MB", "GB"];
    const index = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
    const value = n / Math.pow(1024, index);

    return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}